import { cached } from '@glimmer/tracking';

import type { DSData } from 'webapp-types';
import { Aggregation } from 'webapp/enums/enums';
import { getIndicatorClass } from 'webapp/utils/number';

import type DsDataOutputOther from 'webapp/models-custom/ds-data/output/other';
import DsDataPointBase from 'webapp/models-custom/ds-data/point/base';

export default class DsDataPointOther extends DsDataPointBase {
	output: DsDataOutputOther;

	type: string; // "total", "min", "max", "avg"

	constructor(output: DsDataOutputOther, rawData: DSData.PointOther) {
		super(rawData);
		this.output = output;
		this.type = rawData.type;
	}

	get typeEnum(): Aggregation {
		switch (this.type) {
			case 'total':
				return Aggregation.SUM;
			case 'min':
				return Aggregation.MIN;
			case 'max':
				return Aggregation.MAX;
			case 'avg':
				return Aggregation.AVG;
			case 'median':
				return Aggregation.MEDIAN;
			default:
				return Aggregation.SUM;
		}
	}

	@cached
	get comparePoints() {
		// only returned if this is "other" of the TopLevel
		const comparePoints: DsDataPointOther[] = [];
		this.output.compareOutputs.forEach((compareOutput) => {
			const comparePoint = compareOutput.points.find((item) => item.type === this.type);
			if (comparePoint) {
				comparePoints.push(comparePoint);
			}
		});
		return comparePoints;
	}

	@cached
	get comparePointPrimary(): DsDataPointOther | null {
		// only returned if this is "other" of the Compare
		let primaryPoint: DsDataPointOther | null = null;
		this.output.topOutputs.some((output) => {
			const point = output.points.find((item) => item.type === this.type);
			if (point) {
				primaryPoint = point;
			}
			return !!primaryPoint;
		});
		return primaryPoint;
	}

	@cached
	get change(): number | null {
		const primaryValue = this.comparePointPrimary?.value || null,
			thisValue = this.value || null;

		let compareValue: number | null = null;

		if (thisValue && typeof primaryValue === 'number') {
			// round to 1 number after comma
			compareValue = ((primaryValue - thisValue) / Math.abs(thisValue)) * 100;
			compareValue = Math.round(compareValue * 10) / 10;
		}

		return compareValue;
	}

	@cached
	get changeStr() {
		const compare = this.change;

		let valueStr = '';

		if (typeof compare === 'number') {
			if (compare < 0) {
				valueStr = '▼ ';
			} else if (compare > 0) {
				valueStr = '▲ ';
			}
			valueStr += String(Math.abs(compare)) + '%';
		}

		return valueStr;
	}

	@cached
	get indicatorClass() {
		const primaryPoint = this.comparePointPrimary,
			change = (primaryPoint?.value || 0) - this.value;
		if (primaryPoint && typeof change === 'number') {
			return getIndicatorClass(change, primaryPoint.output.invert);
		} else {
			return '';
		}
	}
}
